import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeatureDemoStep = ({ userType, onNext, onBack }) => {
  const [currentDemo, setCurrentDemo] = useState(0);

  const features = [
    {
      id: 'daily-checkin',
      title: 'Daily Check-In',
      icon: 'Calendar',
      description: 'Track how you feel every day with our intuitive symptom logging',
      demo: {
        type: 'interactive',
        content: 'Log your symptoms in seconds with our guided check-in process'
      },
      benefits: [
        'Quick and easy symptom logging',
        'Visual body mapping',
        'Voice notes and photo attachments'
      ]
    },
    {
      id: 'analytics',
      title: 'Health Analytics',
      icon: 'TrendingUp',
      description: 'Visualize your health patterns and identify trends over time',
      demo: {
        type: 'visual',
        content: 'See your symptom patterns with beautiful charts and insights'
      },
      benefits: [
        'Identify symptom triggers',
        'Track medication effectiveness',
        'Understand your health patterns'
      ]
    },
    {
      id: 'reports',
      title: 'Doctor Reports',
      icon: 'FileText',
      description: 'Generate comprehensive reports to share with your healthcare providers',
      demo: {
        type: 'preview',
        content: 'Export detailed summaries for your medical appointments'
      },
      benefits: [
        'Professional medical summaries',
        'Customizable date ranges',
        'PDF and email export'
      ]
    }
  ];

  const currentFeature = features?.[currentDemo];

  const handleNext = () => {
    if (currentDemo < features?.length - 1) {
      setCurrentDemo(currentDemo + 1);
    } else {
      onNext();
    }
  };

  const handlePrevious = () => {
    if (currentDemo > 0) {
      setCurrentDemo(currentDemo - 1);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          Discover Key Features
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Let's walk through the main features you'll use every day
        </p>
      </div>

      {/* Feature Demo Card */}
      <div className="max-w-3xl mx-auto">
        <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name={currentFeature?.icon} size={32} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-foreground mb-2">{currentFeature?.title}</h3>
              <p className="text-muted-foreground">{currentFeature?.description}</p>
            </div>
          </div>

          {/* Demo Preview */}
          <div className="bg-muted rounded-lg p-8 mb-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <Icon name={currentFeature?.icon} size={40} className="text-primary" />
            </div>
            <p className="text-lg text-foreground font-medium">{currentFeature?.demo?.content}</p>
          </div>

          {/* Benefits */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground">Key Benefits:</h4>
            <div className="grid grid-cols-1 gap-2">
              {currentFeature?.benefits?.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-background rounded-lg p-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Dots */}
      <div className="flex items-center justify-center gap-2">
        {features?.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentDemo(idx)}
            className={`h-2 rounded-full transition-all ${
              idx === currentDemo ? 'w-8 bg-primary' : 'w-2 bg-muted hover:bg-muted-foreground/30'
            }`}
            aria-label={`Go to feature ${idx + 1}`}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
        <Button
          variant="outline"
          onClick={currentDemo === 0 ? onBack : handlePrevious}
          iconName="ArrowLeft"
          className="sm:flex-1"
        >
          {currentDemo === 0 ? 'Back' : 'Previous'}
        </Button>
        <Button
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          {currentDemo === features?.length - 1 ? 'Continue' : 'Next Feature'}
        </Button>
      </div>

      <div className="text-center">
        <button
          onClick={onNext}
          className="text-sm text-primary hover:underline"
        >
          Skip feature tour
        </button>
      </div>
    </div>
  );
};

export default FeatureDemoStep;